// Configurable instrument bar at the bottom of the map.
//
// `useInstrumentContext` aggregates everything an InstrumentDef.compute()
// might need: GPS fix + previous fix (for derivatives), active flight
// plan, active aircraft profile, async-resolved terrain elevation,
// nearest METAR baro setting, nearest airport / navaid.
//
// `InstrumentSelector` is the bottom-sheet picker that opens when the
// user taps the strip. Lists every catalog entry grouped by category;
// up to MAX_INSTRUMENTS can be selected; Wave-2 placeholders render as
// locked rows with a tooltip.

import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useGpsStore, type GpsFix } from '../stores/gpsStore';
import { useTrackStore } from '../stores/trackStore';
import { usePlanStore } from '../stores/planStore';
import { useAircraftStore, activeCruise } from '../stores/aircraftStore';
import { greatCircleNm } from '../lib/geo';
import { computeActiveLeg } from '../lib/activeLeg';
import { fetchBbox } from '../lib/mapBbox';
import {
  INSTRUMENT_CATALOG,
  INSTRUMENT_BY_ID,
  MAX_INSTRUMENTS,
  groupOrder,
  groupLabel,
  type InstrumentContext,
  type InstrumentId,
  type InstrumentGroup,
} from '../lib/instruments';

/**
 * Aggregates everything every InstrumentDef.compute() might need.
 *
 * Re-renders whenever any of the inputs change:
 *   - GPS fix (live)
 *   - Active flight plan route / cruiseAlt
 *   - Active aircraft profile
 *   - Track recording state
 * Plus async fetches refreshed at coarser cadence:
 *   - Terrain elevation: every 30 sec OR when the position drifts > 0.05\u00b0
 *   - Nearest METAR baro: every 5 minutes
 *   - Nearest airport / navaid: every 60 sec
 */
export function useInstrumentContext(): InstrumentContext {
  const fix = useGpsStore((s) => s.fix);
  const route = usePlanStore((s) => s.route);
  const cruiseAlt = usePlanStore((s) => s.cruiseAlt);
  const aircraftId = usePlanStore((s) => s.aircraftId);
  const aircraftList = useAircraftStore((s) => s.list);
  const aircraft = aircraftList.find((a) => a.id === aircraftId) ?? null;
  const recording = useTrackStore((s) => s.recording);
  const startedAtMs = useTrackStore((s) => s.current?.startedAt ?? null);

  // Previous fix for VS / rate-of-turn. Kept in a ref so changing it\n  // doesn't itself force a re-render.
  const prevFixRef = useRef<GpsFix | null>(null);
  const lastFixSeenRef = useRef<GpsFix | null>(null);
  if (fix && lastFixSeenRef.current !== fix) {
    prevFixRef.current = lastFixSeenRef.current;
    lastFixSeenRef.current = fix;
  }

  // Async-resolved fields. We keep them in component state so the
  // InstrumentDef.compute() functions stay synchronous + cheap.
  const [terrainElevFt, setTerrainElevFt] = useState<number | null>(null);
  const [baroInHg, setBaroInHg] = useState<number | null>(null);
  const [baroStation, setBaroStation] = useState<string | null>(null);
  const [nearestApt, setNearestApt] = useState<{ ident: string; distNm: number } | null>(null);
  const [nearestNav, setNearestNav] = useState<{ ident: string; distNm: number; type: string } | null>(null);
  // Tracks the magnetic variation (east-positive degrees) sourced
  // from the nearest NASR navaid. Falls back to a coarse latitude-
  // based estimate when no nearby navaid is found. See instruments.ts
  // for how this flows into TRK / CRS / HDG instrument compute fns.
  const [magVarDeg, setMagVarDeg] = useState<number>(15);

  // Throttled refetch keys: only re-fire when the rounded position
  // crosses a coarse grid square (saves us from hammering APIs while
  // the GPS fix jitters by sub-meter amounts).
  const terrainKey = fix ? `${fix.lat.toFixed(2)},${fix.lon.toFixed(2)}` : null;
  const baroKey    = fix ? `${(Math.round(fix.lat * 4) / 4).toFixed(2)},${(Math.round(fix.lon * 4) / 4).toFixed(2)}` : null;
  const nearestKey = fix ? `${(Math.round(fix.lat * 10) / 10).toFixed(1)},${(Math.round(fix.lon * 10) / 10).toFixed(1)}` : null;

  // ---- Terrain elevation ----
  useEffect(() => {
    if (!fix || !terrainKey) { setTerrainElevFt(null); return; }
    let cancelled = false;
    (async () => {
      try {
        // Open-Meteo's elevation endpoint reuses the same upstream we\n        // hit for winds. Good enough for AGL display.
        const r = await fetch(
          `https://api.open-meteo.com/v1/elevation?latitude=${fix.lat.toFixed(4)}&longitude=${fix.lon.toFixed(4)}`
        );
        if (!r.ok || cancelled) return;
        const d = (await r.json()) as { elevation?: number[] };
        const m = d.elevation?.[0];
        if (m == null) return;
        setTerrainElevFt(m * 3.28084);
      } catch {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [terrainKey, fix?.lat, fix?.lon]);

  // ---- Nearest METAR altimeter setting (via /api/wx/metar-nearest proxy
  //      so the browser doesn't trip aviationweather.gov's CORS policy) ----
  useEffect(() => {
    if (!fix || !baroKey) { setBaroInHg(null); setBaroStation(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `/api/wx/metar-nearest?lat=${fix.lat.toFixed(4)}&lon=${fix.lon.toFixed(4)}`
        );
        if (!r.ok || cancelled) return;
        const d = (await r.json()) as { station: string | null; altimInHg: number | null };
        if (cancelled) return;
        setBaroInHg(d.altimInHg);
        setBaroStation(d.station);
      } catch {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [baroKey, fix?.lat, fix?.lon]);

  // ---- Nearest airport + navaid ----
  useEffect(() => {
    if (!fix || !nearestKey) { setNearestApt(null); setNearestNav(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const pad = 0.5;
        const args = {
          west: fix.lon - pad, east: fix.lon + pad,
          south: fix.lat - pad, north: fix.lat + pad,
          zoom: 10,
        };
        const [aps, navs] = await Promise.all([
          fetchBbox({ ...args, layer: 'airports', limit: 100 }).catch(() => []),
          fetchBbox({ ...args, layer: 'navaids',  limit: 50 }).catch(() => []),
        ]);
        if (cancelled) return;
        const apsSorted = aps
          .map((a) => ({ ident: a.ident, distNm: greatCircleNm(fix.lat, fix.lon, a.lat, a.lon) }))
          .sort((a, b) => a.distNm - b.distNm);
        const navsSorted = navs
          .map((n) => ({
            ident: n.ident,
            distNm: greatCircleNm(fix.lat, fix.lon, n.lat, n.lon),
            type: n.type,
            magVar: typeof n.magVar === 'number' ? n.magVar : null,
          }))
          .sort((a, b) => a.distNm - b.distNm);
        setNearestApt(apsSorted[0] ?? null);
        setNearestNav(navsSorted[0] ?? null);
        // Pull magvar from the nearest navaid that publishes one (the
        // closest navaid almost always does; we walk the sorted list
        // for safety). Boss report 2026-05-13: Fyrefly was showing TRK
        // 345 vs Garmin's 331 in the Yreka / Weed area — textbook 14°E
        // magnetic variation. Hardcoded magVarDeg of 15 was approximate
        // for the whole west coast and we never read the real NASR
        // value despite having it on the wire.
        const navWithMagVar = navsSorted.find((n) => n.magVar != null);
        if (navWithMagVar?.magVar != null) {
          setMagVarDeg(navWithMagVar.magVar);
        }
      } catch {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [nearestKey, fix?.lat, fix?.lon]);

  // ---- Compute next-waypoint index ----
  // Shared helper consumed by both the instrument bar and the map
  // route-line color renderer. See src/lib/activeLeg.ts.
  const { nextWpIdx } = computeActiveLeg(fix, route);

  // ---- Aircraft cruise TAS (currently only used for descent calc;\n  //      can wire elsewhere later) ----
  const { cruiseKt: cruiseKtMaybe } = activeCruise(aircraft);
  const cruiseKt = cruiseKtMaybe ?? null;

  return {
    fix,
    prevFix: prevFixRef.current,
    flightStartedAtMs: recording ? startedAtMs : null,
    route,
    cruiseAlt,
    cruiseKt,
    nextWpIdx,
    terrainElevFt,
    baroInHg,
    baroStation,
    nearestAirport: nearestApt,
    nearestNavaid: nearestNav,
    // Sourced from the nearest NASR navaid's published magVar (east-
    // positive). Falls back to 15 when no navaid lookup has resolved
    // yet OR no nearby navaid publishes a magVar. The default is
    // intentionally close to the US-CONUS average so any momentary
    // pre-resolve render is roughly right.
    magVarDeg,
  };
}

// ── Context provider ─────────────────────────────────────────────────────
// (Claude audit 2026-05-18, finding 1) The hook above is heavy: it
// subscribes to 5 stores AND owns three async fetches (terrain elev,
// METAR baro, fetchBbox airports + navaids). Previously each of
// MapPage's DataStrip / AlertTriggerWatcher / AltitudeSliderHost
// called the hook directly, which meant three independent throttle
// states, three component re-renders per GPS tick, and three
// duplicate /api/wx/metar-nearest + fetchBbox calls per grid crossing.
//
// The fix: wrap MapPage in <InstrumentContextProvider> which runs the
// hook ONCE and broadcasts the value via React context. Children call
// useInstrumentContextValue() instead of useInstrumentContext().
//
// useInstrumentContext is still exported so non-MapPage callers can
// still mount it locally if they ever appear.

const InstrumentCtx = createContext<InstrumentContext | null>(null);

export function InstrumentContextProvider({ children }: { children: ReactNode }) {
  const value = useInstrumentContext();
  return <InstrumentCtx.Provider value={value}>{children}</InstrumentCtx.Provider>;
}

/**
 * Read the InstrumentContext from the nearest <InstrumentContextProvider>.
 * Throws if the consumer is mounted outside a provider — fail loud so we
 * don't silently fall back to per-consumer hook copies (the bug we're
 * fixing). If you need a one-off InstrumentContext in a tree without a
 * provider, call `useInstrumentContext()` directly.
 */
export function useInstrumentContextValue(): InstrumentContext {
  const ctx = useContext(InstrumentCtx);
  if (!ctx) {
    throw new Error(
      'useInstrumentContextValue must be used inside <InstrumentContextProvider>. ' +
      'Use useInstrumentContext() directly if you really need a per-consumer copy.',
    );
  }
  return ctx;
}

// --- InstrumentSelector --------------------------------------------------

export function InstrumentSelector({
  selected, onChange, onClose,
}: {
  selected: InstrumentId[];
  onChange: (next: InstrumentId[]) => void;
  onClose: () => void;
}) {
  // Group the catalog for rendering. We sort groups, then sort within
  // each group by the catalog declaration order.
  const groups = (['position', 'aircraft', 'altitude', 'navigation', 'time', 'environment'] as InstrumentGroup[]).map((g) => ({
    group: g,
    items: INSTRUMENT_CATALOG.filter((d) => d.group === g),
  })).filter((s) => s.items.length > 0).sort((a, b) => groupOrder(a.group) - groupOrder(b.group));

  const toggle = (id: InstrumentId) => {
    const def = INSTRUMENT_BY_ID[id];
    if (!def || !def.available) return;
    const has = selected.includes(id);
    let next: InstrumentId[];
    if (has) {
      next = selected.filter((x) => x !== id);
    } else if (selected.length >= MAX_INSTRUMENTS) {
      // Drop the oldest to make room. Same as uiStore.toggleInstrument.
      next = [...selected.slice(1), id];
    } else {
      next = [...selected, id];
    }
    onChange(next);
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-end md:items-center justify-center"
      onClick={onClose}
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        aria-hidden="true"
      />
      <div
        className="relative w-full md:max-w-lg md:max-h-[80vh] max-h-[78vh] bg-cockpit-900 border border-cockpit-700 md:rounded-xl rounded-t-xl shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-cockpit-700 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-cockpit-100 text-base font-bold">Instrument Bar</h2>
            <p className="text-[11px] text-cockpit-400">
              Pick up to {MAX_INSTRUMENTS} readouts · {selected.length}/{MAX_INSTRUMENTS} selected
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-cockpit-300 hover:text-cockpit-100 text-xl leading-none px-2"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-2 py-1">
          {groups.map((s) => (
            <section key={s.group} className="mb-3">
              <div className="px-2 pt-2 pb-1 text-[10px] tracking-widest text-cockpit-400">
                {groupLabel(s.group).toUpperCase()}
              </div>
              <ul>
                {s.items.map((def) => {
                  const isSelected = selected.includes(def.id);
                  const locked = !def.available;
                  return (
                    <li key={def.id}>
                      <button
                        type="button"
                        disabled={locked}
                        onClick={() => toggle(def.id)}
                        className={[
                          'w-full text-left px-3 py-2 rounded-md flex items-start gap-3 transition-colors',
                          locked
                            ? 'opacity-50 cursor-not-allowed'
                            : isSelected
                              ? 'bg-ember-600/15 hover:bg-ember-600/25'
                              : 'hover:bg-cockpit-800',
                        ].join(' ')}
                      >
                        <div className={[
                          'flex-shrink-0 mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center text-[11px]',
                          locked
                            ? 'border-cockpit-700 text-cockpit-600'
                            : isSelected
                              ? 'border-ember-500 bg-ember-500 text-white'
                              : 'border-cockpit-500',
                        ].join(' ')}>
                          {locked ? '\u{1F512}' : (isSelected ? '\u2713' : '')}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm text-cockpit-100">{def.label}</span>
                            <span className="text-[10px] font-mono text-cockpit-400">{def.shortLabel}</span>
                            {locked && (
                              <span className="text-[9px] uppercase tracking-widest bg-cockpit-700 text-cockpit-300 px-1.5 py-0.5 rounded">
                                Coming
                              </span>
                            )}
                          </div>
                          {def.description && (
                            <div className="text-[11px] text-cockpit-400 mt-0.5">{def.description}</div>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>

        <div className="px-4 py-3 border-t border-cockpit-700 flex justify-end flex-shrink-0">
          <button
            onClick={onClose}
            className="bg-ember-600 hover:bg-ember-500 text-white text-sm rounded-lg px-4 py-2"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
